import { InvalidArgumentError, Option } from 'commander';
import type { CommandInitFunction } from '../../types/CommandInitFunction.js';
import type { StartOptions } from './types/StartOptions.js';
import { portNumbers } from 'get-port';
import { reusedOptions } from '../../utilities/reusedOptions.js';

// verify the values are correct
const showOverlayChoices: NonNullable<StartOptions['showOverlay']>[] = ['always', 'errors-only', 'never'];

/** Defines the "start" verb. */
export const init: CommandInitFunction = (program) => {
  program.addSubCommand<StartOptions>({
    name: 'start',
    description: 'Starts a web inner-loop for an app.',
    options: {
      app: reusedOptions.app(),
      open: [
        new Option('--open [url]', 'Open the browser (to the URL if provided).'),
        new Option('--no-open', 'Do not open the browser.'),
      ],
      cache: reusedOptions.cache(),
      mode: reusedOptions.mode({ action: 'run' }),
      showOverlay: new Option(
        '--show-overlay <when>',
        'Override whether to show the Cloudpack overlay (status badge and dialog) in the app UI.',
        // Don't specify a default for this option to avoid unconditionally overriding any value
        // set in the ServerConfig
      ).choices(showOverlayChoices),
      logResolveMap: reusedOptions.logResolveMap(),
      logBundleInfo: reusedOptions.logBundleInfo({ requiresNoCache: true }),
      apiServerPort: getPortOption('--api-server-port <port>', 'API', true),
      appServerPort: getPortOption('--app-server-port <port>', 'app'),
      bundleServerPort: getPortOption('--bundle-server-port <port>', 'bundle'),
      login: reusedOptions.login('start'),
    },
    applyOptionsToConfig: ({ showOverlay, open }) => {
      // These will be ignored if no values are set
      return { extraServerConfig: { showOverlay, open } };
    },
    getExecutor: () => import('./execute.js'),
    // If cwd isn't a cloudpack app, discover other cloudpack apps and possibly prompt the user
    // to choose one. Also handles --app.
    discoverAppPaths: 'single',
    enableReporterReset: true,
  });
};

function getPortOption(flags: string, name: string, isFirst?: boolean): Option {
  return new Option(
    flags,
    `Port for the ${name} server (${isFirst ? 'single, comma-separated, or range with dash' : 'as above'}).`,
  ).argParser(parsePorts);
}

function parsePorts(value: string): number[] {
  if (value.includes('-')) {
    const split = value.split('-');
    if (split.length !== 2) {
      throw new InvalidArgumentError(`Invalid port range: "${value}"`);
    }
    return [...portNumbers(parseNumber(split[0]), parseNumber(split[1]))];
  }

  return value.split(',').map(parseNumber);
}

function parseNumber(value: string): number {
  value = value.trim();
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    throw new InvalidArgumentError(`Port must be a number; received: "${value}"`);
  }
  return parsed;
}
